import { Link } from "@tanstack/react-router";
import { ArrowRight, MessageCircle, Award, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { whatsappLink, SITE } from "@/lib/site";
import heroImg from "@/assets/tratamento-pes-belem-para-hero.jpg";

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroImg}
          alt="Tratamento dos pés em Belém do Pará — Clínica Podomasso"
          className="h-full w-full object-cover"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/65 to-primary/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto flex min-h-[88vh] max-w-7xl items-center px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 backdrop-blur-md"
          >
            <Award className="h-4 w-4 text-white" />
            <span className="text-xs font-medium uppercase tracking-wider text-white">
              Clínica de Podologia em Belém do Pará
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            Cuide dos Seus Pés com Quem Entende do Assunto
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg lg:text-xl"
          >
            Tratamentos especializados para unhas encravadas, calos, micoses, pés diabéticos e
            muito mais. Recupere o conforto e a saúde dos seus pés com a podóloga{" "}
            <strong className="text-white">{SITE.professional.name}</strong>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Button asChild variant="whatsapp" size="xl">
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5" />
                Agende pelo WhatsApp
              </a>
            </Button>
            <Button asChild variant="soft" size="xl">
              <Link to="/servicos">
                Conheça os Tratamentos
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-6 text-sm text-white/90"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary-glow" />
              <div>
                <p className="font-semibold">Atendimento {SITE.hours.days}</p>
                <p className="text-xs text-white/70">{SITE.hours.open} às {SITE.hours.close}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary-glow" />
              <div>
                <p className="font-semibold">{SITE.professional.registry}</p>
                <p className="text-xs text-white/70">{SITE.professional.name} — Podóloga Responsável</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display text-3xl font-bold text-primary-glow">10+</span>
              <p className="text-xs leading-tight text-white/80">
                Anos de
                <br />
                Experiência
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
